import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { CheckCircle2, AlertTriangle, Info, Loader2, Play, RefreshCw } from 'lucide-react';
import { ValidationResultReport } from '../../../api/apiTypes';
import { validateReportPrerequisites, indicizzaReport } from '../../../api/functionApi';
import { classValidation, classLabels, getLabelSpec, isBlockingLabel } from '../../../utils/validationLabels';

interface Props {
  /** Classe documento di cui mostrare lo stato. */
  documentType: string;
  /** Presente solo per le classi bancarie: /validate lavora per id di banca. */
  bankId?: string | null;
  /** Nome della banca: /ingest lavora per nome, non per id. */
  bankName?: string | null;
  /**
   * Cambia quando qualcosa a monte ha modificato le fonti (tipicamente un
   * caricamento): fa ripetere /validate senza che il chiamante conosca il metodo.
   */
  refreshToken: number;
}

/**
 * Stato di validazione della classe, aggiornato dopo ogni modifica alle fonti.
 *
 * /validate e' in sola lettura — non scarica e non indicizza nulla — quindi si
 * puo' chiamare a ogni apertura del pannello. L'indicizzazione invece resta un
 * gesto esplicito: parte per l'intera banca, non per la singola fonte appena
 * caricata, quindi non va mai innescata da sola.
 */
const ClassValidationStrip: React.FC<Props> = ({
  documentType,
  bankId,
  bankName,
  refreshToken,
}) => {
  const [risultato, setRisultato] = useState<ValidationResultReport | null>(null);
  const [caricamento, setCaricamento] = useState(false);
  const [nonDisponibile, setNonDisponibile] = useState(false);
  const [indicizzando, setIndicizzando] = useState(false);

  const valida = useCallback(async () => {
    setCaricamento(true);
    try {
      const grezzo = await validateReportPrerequisites(bankId ? [bankId] : []);
      // Stessa normalizzazione del wizard: le banche arrivano come chiavi di
      // primo livello, accanto a `status` e `general`.
      setRisultato({
        status: grezzo.status,
        general: grezzo.general,
        banks: Object.fromEntries(
          Object.entries(grezzo).filter(([k]) => k !== 'status' && k !== 'general'),
        ),
      });
      setNonDisponibile(false);
    } catch (err) {
      // Qui la validazione e' un di piu': se il server la rifiuta (per esempio
      // con una lista di banche vuota) la striscia sparisce, non si urla.
      console.warn('Validazione classe non disponibile:', err);
      setNonDisponibile(true);
    } finally {
      setCaricamento(false);
    }
  }, [bankId]);

  useEffect(() => {
    valida();
  }, [valida, refreshToken]);

  const validazione = classValidation(risultato, documentType);
  const labels = classLabels(validazione);
  const daIndicizzare = labels.includes('indexation_needed');

  const indicizza = async () => {
    if (!bankName) return;
    setIndicizzando(true);
    try {
      const esito = await indicizzaReport([bankName], false);
      const avviate = esito.triggered_pipelines.length;
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: avviate > 0 ? 'success' : 'info',
        title:
          avviate > 0
            ? `${avviate} indicizzazion${avviate === 1 ? 'e avviata' : 'i avviate'}`
            : 'Nessuna indicizzazione avviata',
        text:
          avviate > 0
            ? undefined
            : `${esito.skipped_sources} di ${esito.total_sources} unità saltate`,
        showConfirmButton: false,
        timer: 3500,
        timerProgressBar: true,
      });
      await valida();
    } catch (err) {
      Swal.fire(
        'Errore',
        err instanceof Error ? err.message : 'Impossibile avviare l’indicizzazione',
        'error',
      );
    } finally {
      setIndicizzando(false);
    }
  };

  if (nonDisponibile) return null;

  if (caricamento && !risultato) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-textMuted">
        <Loader2 className="w-3 h-3 animate-spin" />
        Verifica dello stato…
      </div>
    );
  }

  if (!validazione) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {labels.length === 0 ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="w-3 h-3" />
          Pronta per il report
        </span>
      ) : (
        labels.map((l) => {
          const spec = getLabelSpec(l);
          const blocca = isBlockingLabel(l);
          return (
            <span
              key={l}
              title={spec?.action}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${
                blocca
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-gray-50 text-textSecondary border-borderDivider'
              }`}
            >
              {blocca ? <AlertTriangle className="w-3 h-3" /> : <Info className="w-3 h-3" />}
              {spec?.label ?? l}
            </span>
          );
        })
      )}

      {caricamento && <Loader2 className="w-3 h-3 animate-spin text-textMuted" />}

      {daIndicizzare && bankName && (
        <button
          type="button"
          onClick={indicizza}
          disabled={indicizzando}
          title="Avvia l'indicizzazione delle unità non ancora indicizzate di questa banca e delle classi generali"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-borderDivider text-[11px] font-medium text-textSecondary hover:border-textPrimary/40 hover:text-textPrimary transition-all disabled:opacity-50"
        >
          {indicizzando ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Play className="w-3 h-3" />
          )}
          {indicizzando ? 'Avvio…' : 'Indicizza ora'}
        </button>
      )}

      <button
        type="button"
        onClick={valida}
        disabled={caricamento}
        title="Ricontrolla lo stato della classe"
        className="inline-flex items-center text-textMuted hover:text-textPrimary transition-colors disabled:opacity-40"
      >
        <RefreshCw className="w-3 h-3" />
      </button>
    </div>
  );
};

export default ClassValidationStrip;
